import { useMemo } from "react";
import { User } from "lucide-react";
import { API_BASE } from "@/lib/api";

/**
 * Square profile avatar with a bordered frame that matches the SalesArena aesthetic.
 * - If `avatarUrl` is set, shows the image.
 * - Otherwise, shows a faint silhouette icon with the user's initials centered over it.
 *
 * Props:
 *   name          — used to derive fallback initials & the tinted background color.
 *   avatarUrl     — path from the API (e.g. "/api/users/avatars/…") OR full URL.
 *   initials      — override for the auto-derived initials (from the leaderboard payload).
 *   size          — "xs" | "sm" | "md" | "lg" | "xl" | "podium"
 *   rank          — 1|2|3 tints the border gold/silver/bronze (for podium spots).
 *   className     — extra classes appended to the outer element.
 */
export function UserAvatar({
  name = "",
  avatarUrl,
  initials,
  size = "md",
  rank,
  className = "",
  testid,
}) {
  const dims = SIZE_MAP[size] || SIZE_MAP.md;
  const fallbackInitials = initials || deriveInitials(name);
  const tint = useMemo(() => nameTint(name), [name]);
  const rankBorder = RANK_BORDER[rank] || "border-[#27272A]";
  const src = normalizeAvatarUrl(avatarUrl);

  const isFill = size === "fill";
  return (
    <div
      className={`relative shrink-0 overflow-hidden ${isFill ? "" : `rounded-sm border-2 ${rankBorder}`} ${dims.box} ${className}`}
      style={!src ? { backgroundColor: tint.bg } : undefined}
      data-testid={testid}
      aria-label={name ? `${name} avatar` : "User avatar"}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      ) : (
        <>
          {/* Faded silhouette background */}
          <User
            className={`absolute inset-0 m-auto ${dims.silhouette}`}
            style={{ color: tint.silhouette }}
            strokeWidth={1.25}
          />
          {/* Initials on top */}
          <span
            className={`relative z-10 flex items-center justify-center w-full h-full font-display tracking-wide ${dims.text}`}
            style={{ color: tint.fg }}
          >
            {fallbackInitials}
          </span>
        </>
      )}
    </div>
  );
}

const SIZE_MAP = {
  xs: { box: "w-6 h-6", text: "text-[10px]", silhouette: "w-4 h-4" },
  sm: { box: "w-8 h-8", text: "text-xs", silhouette: "w-5 h-5" },
  md: { box: "w-10 h-10", text: "text-sm", silhouette: "w-7 h-7" },
  lg: { box: "w-14 h-14", text: "text-lg", silhouette: "w-9 h-9" },
  xl: { box: "w-20 h-20", text: "text-2xl", silhouette: "w-12 h-12" },
  podium: { box: "w-16 h-16 md:w-20 md:h-20", text: "text-xl md:text-2xl", silhouette: "w-10 h-10 md:w-12 md:h-12" },
  fill: { box: "w-full h-full", text: "text-4xl md:text-5xl", silhouette: "w-1/2 h-1/2" },
};

const RANK_BORDER = {
  1: "border-[#FFC832]",
  2: "border-[#C0C0C0]",
  3: "border-[#CD7F32]",
};

// Deterministic on-brand tint per name so avatars don't all look identical
const TINT_PALETTE = [
  { bg: "#0F1A2B", fg: "#41A0E8", silhouette: "rgba(65,160,232,0.18)" },
  { bg: "#1F0F0F", fg: "#FF6B57", silhouette: "rgba(255,107,87,0.18)" },
  { bg: "#0F1F14", fg: "#34C759", silhouette: "rgba(52,199,89,0.18)" },
  { bg: "#241A0F", fg: "#E8921F", silhouette: "rgba(232,146,31,0.18)" },
  { bg: "#1A0F24", fg: "#C58AF9", silhouette: "rgba(197,138,249,0.18)" },
  { bg: "#241F0F", fg: "#FFC832", silhouette: "rgba(255,200,50,0.18)" },
  { bg: "#0F2124", fg: "#5AC8FA", silhouette: "rgba(90,200,250,0.18)" },
];

function nameTint(name = "") {
  if (!name) return TINT_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return TINT_PALETTE[hash % TINT_PALETTE.length];
}

function deriveInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function normalizeAvatarUrl(url) {
  if (!url) return null;
  // Backend returns "/api/users/avatars/..." — prepend backend origin for cross-origin cookies.
  if (url.startsWith("/api/")) {
    // API_BASE already ends in "/api" so trim the leading "/api" of url
    return `${API_BASE}${url.slice(4)}`;
  }
  return url;
}
